import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  DOSSIER_SCENARIO_LABELS,
  DossierScenarioSchema,
  type DossierDto,
} from '../../lib/api-types';
import type { DossierUpdateInput } from '../../lib/api';
import { useMandants } from '../mandants/hooks';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Select } from '../../components/ui/Select';

const editSchema = z.object({
  mandantId: z.string().min(1, 'Veuillez choisir un mandant.'),
  scenario: DossierScenarioSchema,
  objet: z.string().optional(),
});
type EditValues = z.infer<typeof editSchema>;

export function DossierEditForm({
  dossier,
  onSubmit,
  onCancel,
  pending,
}: {
  dossier: DossierDto;
  onSubmit: (input: DossierUpdateInput) => void;
  onCancel: () => void;
  pending?: boolean;
}): JSX.Element {
  const mandantsQuery = useMandants({ page: 1, pageSize: 100, includeInactive: true });
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<EditValues>({
    resolver: zodResolver(editSchema),
    defaultValues: {
      mandantId: dossier.mandantId,
      scenario: dossier.scenario,
      objet: dossier.objet ?? '',
    },
  });

  const submit = handleSubmit((values) => {
    onSubmit({
      mandantId: values.mandantId,
      scenario: values.scenario,
      objet: values.objet ?? '',
    });
  });

  return (
    <form onSubmit={submit} className="space-y-4" noValidate>
      <div>
        <Label htmlFor="edit-mandant">Mandant</Label>
        <Select id="edit-mandant" {...register('mandantId')}>
          {(mandantsQuery.data?.items ?? []).map((m) => (
            <option key={m.id} value={m.id}>
              {m.nom}
            </option>
          ))}
        </Select>
        {errors.mandantId ? (
          <p className="mt-1 text-sm text-red-600">{errors.mandantId.message}</p>
        ) : null}
      </div>
      <div>
        <Label htmlFor="edit-scenario">Scénario</Label>
        <Select id="edit-scenario" {...register('scenario')}>
          {DossierScenarioSchema.options.map((s) => (
            <option key={s} value={s}>
              {DOSSIER_SCENARIO_LABELS[s]}
            </option>
          ))}
        </Select>
      </div>
      <div>
        <Label htmlFor="edit-objet">Objet</Label>
        <Input id="edit-objet" {...register('objet')} />
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
          Annuler
        </Button>
        <Button type="submit" loading={pending}>
          Enregistrer
        </Button>
      </div>
    </form>
  );
}
